'use client';
import { useCallback, useMemo, useState } from 'react';

import { LinkButtonProps } from '@/components/atoms/linkButton';
import LinkButtonSB, {
  LinkButtonSBProps,
} from '@/components/atoms/linkButton/LinkSB';
import { Toggles, TogglesProps } from '@/components/atoms/toggle';

export type ToggleCMSProps = {
  toggles: {
    name: string;
    startsWith: number;
    endsWith: number;
    button?: LinkButtonSBProps;
  }[];
};

export function useCMSToggle<TList extends any[]>(
  list: TList,
  toggle?: ToggleCMSProps
) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const button = toggle?.toggles?.[selectedIndex]?.button;

  const getGroupedList = useMemo(() => {
    const togglesLength = toggle?.toggles.length || 0;

    const separatedList: TList[] = [];

    if (!togglesLength) {
      return separatedList;
    }

    for (let i = 0; i < togglesLength; i++) {
      const toggleItem = toggle!.toggles[i];
      const toggleList = list.slice(
        toggleItem.startsWith - 1,
        toggleItem.endsWith
      ) as TList;

      if (toggleList.length) {
        separatedList.push(toggleList);
      }
    }

    return separatedList;
  }, [JSON.stringify(list), JSON.stringify(toggle)]);

  const toggleNames = useMemo(
    () => toggle?.toggles.map((toggle) => toggle.name),
    [JSON.stringify(toggle)]
  );

  const handleChange = useCallback((newIndex: number) => {
    setSelectedIndex(newIndex);
  }, []);

  const toggleActionButton = useCallback(
    ({ inner = {} }: { inner?: Partial<LinkButtonProps> }) => {
      if (!button) return null;

      return <LinkButtonSB block={button} inner={inner} />;
    },
    [JSON.stringify(button)]
  );

  const toggleNode = useCallback(
    (toggleProps: Partial<TogglesProps> = {}) =>
      toggleNames ? (
        <Toggles
          {...toggleProps}
          options={toggleNames}
          onChange={(e, newIndex) => handleChange(newIndex)}
          selectedIndex={selectedIndex}
        />
      ) : null,
    [JSON.stringify(toggleNames), selectedIndex]
  );

  if (!toggle)
    return {
      toggleNode: null,
      filteredToggleList: null,
    };

  return {
    toggleNode: toggleNode,
    filteredToggleList: getGroupedList[selectedIndex],
    ToggleActionButton: button ? toggleActionButton : undefined,
  };
}
